import {Component, computed, inject, Input, model, OnInit, signal, ViewChild, WritableSignal} from '@angular/core';
import moment from 'moment';
import {State} from 'frontend/src/app/modules/outage/classes/state';
import {OutageService} from 'frontend/src/app/modules/outage/services/outage.service';
import {BaseElement} from '../../../../classes/base-element';
import {ElementOptionsConfig} from '../../../../classes/element-option';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {SummaryPeak} from 'frontend/src/app/modules/outage/classes/summary-peak';
import {toObservable} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs/operators';
import {SystemEventService} from '../../../../../system-event/services/system-event.service';
import {ExportComponents} from '../../../../../../enums/export-components.enum';
import {FileDownload} from '../../../../../../classes/file-download';

@Component({
    selector: 'eaglei-sit-rep-table-element',
    templateUrl: './sit-rep-table-element.component.html',
    styleUrls: ['../sit-rep-element.scss', './sit-rep-table-element.component.scss'],
    standalone: false,
})
export class SitRepTableElementComponent extends BaseElement implements OnInit {
    private outageService = inject(OutageService);

    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    // Inputs
    protected type = model.required<'state' | 'county'>();
    @Input() startDate: moment.Moment;
    @Input() endDate: moment.Moment;
    @Input() states: State[];
    @Input() eventId: number;
    //

    private tableData$: WritableSignal<SummaryPeak[] | undefined> = signal(undefined);
    public $tableData = computed(() => this.tableData$() ?? []);
    public dataSource: MatTableDataSource<SummaryPeak>;

    public columns = computed(() => {
        const baseColumns = ['state', 'currentOutages', 'percentOut', 'peak', 'peakTime', 'percentRestored'];
        if (this.type() === 'county') {
            baseColumns.unshift('county');
        }
        return baseColumns;
    });

    constructor() {
        super();

        toObservable(this.sitRepService.sitRepFilters)
            .pipe(debounceTime(100))
            .subscribe(() => {
                this.getOutageData();
            });
    }

    ngOnInit() {
        if (this.startDate) {
            this.sitRepService.updateFilters({startDate: this.startDate});
        }
        if (this.endDate) {
            this.sitRepService.updateFilters({endDate: this.endDate});
        }
        if (this.states) {
            this.sitRepService.updateFilters({locations: this.states});
        }

        if (this.eventId) {
            this.sitRepService.updateFilters({eventId: this.eventId} as any);
        }
    }

    private getOutageData(): void {
        if (this.filters().locations?.length > 0) {
            const eventId = this.eventId ? this.eventId : this.filters().event?.id;
            this.outageService.getSummaryWithPeak(eventId, this.type() as any, this.filters().startDate, this.filters().endDate).subscribe({
                next: (res) => {
                    // Filter out states not selected by filter
                    const states = this.filters().locations.map((s) => s.stateAbbreviation);

                    this.tableData$.set(res.filter((d) => states.includes(d.stateAbbreviation)));
                    this.initDataSource();
                },
            });
        } else {
            this.tableData$.set(undefined);
        }
    }

    protected configure(): void {
        this.sitRepService.openConfigSideBar(this as unknown as BaseElement, this.configureOptions);
    }

    public updateConfigValues(options: ElementOptionsConfig): void {
        this.configureOptions = options;
        this.getOutageData();
    }

    public initDataSource(): void {
        if (this.dataSource) {
            this.dataSource.data = this.$tableData();
        } else {
            this.dataSource = new MatTableDataSource<SummaryPeak>(this.$tableData());
            this.dataSource.sortingDataAccessor = this.sortAccessor;
            this.dataSource.sort = this.sort;
            this.dataSource.paginator = this.paginator;
        }
    }

    public sortAccessor(data: any, header: string): string | number {
        switch (header) {
            case 'county':
                return data.countyName;
            case 'state':
                return data.stateName;
            case 'currentOutages':
                return data.currentOutages;
            case 'percentOut':
                return data.percentOutages;
            case 'peak':
                return data.peakOutages;
            case 'peakTime':
                return moment(data.peakTime).valueOf();
            case 'percentRestored':
                return data.percentRestored;
        }
    }

    public exportComponent(): void {
        const config = {
            component: ExportComponents.SIT_REP_TABLE,
            filename: `situation-report-${this.type()}-table`,
            inputs: {
                startDate: this.filters().startDate.toISOString(),
                endDate: this.filters().endDate.toISOString(),
                states: this.filters().locations.map((location) => location.abbreviation),
                type: this.type(),
                eventId: this.filters().event.id,
            },
        };

        this.sitRepService.exportComponent([config]).subscribe({
            next: (blobUrl: string) => {
                FileDownload.downloadFile(`situation-report-${this.type()}-table.png`, blobUrl);
            },
            error: (error) => {
                console.error('Unable to export table data', error);
            },
        });
    }
}
